import * as React from 'react';
import {
    Accordion,
    AddIcon,
    Button,
    Checkbox,
    ExclamationCircleIcon,
    Flex,
    FlexItem,
    FormRadioGroup,
    Header,
    ImageLibraryIcon,
    Text,
    TrashCanIcon
} from "@fluentui/react-northstar";
import EditInput from "../../Utils/EditInput";
import RichTextEditor from "../../Utils/RichTextEditor";
import ImageBrowserDialog from "../../Dialogs/ImageBrowserDialog";
import {blobsRootFolderName} from "../../../apis/blobImageApi";
import {ITranslationMessage} from "../../../Interfaces/TranslationMessage";
import {ImageGallery} from "../../../Interfaces/ImageGallery";
import languageObject from "../../../translations";
import CopilotContainer from "./CopilotContainer/CopilotContainer";
import SuggestionsPopup from "./SuggestionsPopup/SuggestionsPopup";
import {withRouter, WithRouterProps} from "../../../services/TelemetryService/withRouter.hook";
import {IMessageChannel} from "../../../Interfaces/Message";
import {WindowsNotification} from "../../WindowsNotification/WindowsNotification";
import {ButtonActionType} from "../../../Interfaces/ButtonMessage";
import {FilePickerButton} from "../../FilePickerButton/FilePickerButton";
import {IFilePickerFile} from "../../FilePickerButton/FilePickerButton.interfaces";

/**
 * Create message props
 */
interface ICreateMessageProps extends WithRouterProps {
    data: ITranslationMessage,
    header: string,
    onTitleChanged: (value: string) => void,
    onSummaryChanged: (value: string) => void,
    onSubMessageChanged: (value: string) => void,
    onAuthorChanged: (value: string) => void,
    onBtnTitleChanged: (index: number, value: string) => void,
    onBtnLinkChanged: (index: number, value: string) => void,
    onBtnScriptChanged: (index: number, value: IFilePickerFile | undefined) => void,
    onBtnActionTypeChanged: (index: number, value: ButtonActionType) => void,
    onAskFeedbackChange: (value: boolean) => void,
    onAddButton: () => void,
    onRemoveButton: (index: number) => void,
    canAddButtons: () => boolean,
    galleryImages: Array<ImageGallery>,
    onImageSelected: (image: ImageGallery | undefined) => void,
    onBannerSelected: (banner: ImageGallery | undefined) => void,
    onRefreshGalleryImages: () => Promise<void>,
    onImagesUpdated: (images: Array<ImageGallery>) => Promise<void>,
    enableCopilot: boolean,
    channel: IMessageChannel,
    gptName: string | undefined,
}

/**
 * Create message state
 */
interface ICreateMessageState {
    showBannerBrowser: boolean,
    showImageBrowser: boolean,
    selectedImageFromBrowser: string,
    openedButtons: Map<string, boolean>
}

/**
 * Create message step
 */
class CreateMessage extends React.Component<ICreateMessageProps, ICreateMessageState> {
    /**
     * Constructor
     * @param props props
     */
    constructor(props: ICreateMessageProps) {
        super(props);

        this.state = {
            showBannerBrowser: false,
            showImageBrowser: false,
            selectedImageFromBrowser: "",
            openedButtons: new Map<string, boolean>()
        }
    }

    componentDidUpdate(prevProps: Readonly<ICreateMessageProps>) {
        if (prevProps.data.buttons.length < this.props.data.buttons.length) this.scrollToBottom();
    }

    handleImageSelection = (image: ImageGallery) => {
        this.props.onImageSelected(image);
        this.setState({showImageBrowser: false});
    }

    handleDeleteImage = () => {
        this.props.onImageSelected(undefined);
    }

    handleDeleteBanner = () => {
        this.props.onBannerSelected(undefined);
    }

    handleImagesUpdate = async (images: Array<ImageGallery>) => {
        await this.props.onImagesUpdated(images);
    }

    handleBannerSelection = (banner: ImageGallery) => {
        this.props.onBannerSelected(banner);
        this.setState({showBannerBrowser: false});
    }

    handleButtonTitleClick = (buttonId: string) => {
        const openedButtons = new Map(this.state.openedButtons);
        openedButtons.set(buttonId, !this.state.openedButtons.get(buttonId));
        this.setState({openedButtons});
    }

    handleShowImageBrowser = () => {
        this.setState({showImageBrowser: true});
    }

    handleShowBannerBrowser = () => {
        this.setState({showBannerBrowser: true});
    }

    isButtonDataValid = (buttonId: string) => {
        const button = this.props.data.buttons.find(b => b.id === buttonId);
        if (!button) return false;
        const isTitleValid = !!button.title && button.title.length > 0;
        const actionType = button.actionType ?? ButtonActionType.Link;
        let isActionValid = false;
        if (actionType === ButtonActionType.Link) {
            isActionValid = !!button.link && button.link.startsWith("https://");
        } else if (actionType === ButtonActionType.Script) {
            isActionValid = !!button.script;
        }
        return isTitleValid && isActionValid;
    }

    areButtonsValid = () => {
        return this.props.data.buttons.every(btn => this.isButtonDataValid(btn.id ?? ""));
    }

    getOpenedButtonsIndexes = () => {
        const indexes = new Array<number>();
        this.props.data.buttons.forEach((button, index) => {
            const isOpen = this.state.openedButtons.get(button.id ?? "") ?? true;
            if (isOpen) indexes.push(index);
        });
        return indexes;
    }

    scrollToBottom = () => {
        const container = document.getElementsByClassName("form_layout")[0];
        if (container) container.scrollTop = container.scrollHeight;
    }

    override render() {
        const isTeamsMessage = this.props.channel === IMessageChannel.Channel_Teams;
        const isGptMessage = this.props.channel === IMessageChannel.Channel_GPTPro;
        const isWindowsMessage = this.props.channel === IMessageChannel.Channel_Windows;
        const maxLength = isTeamsMessage ? 1000 : 600;
        return (
            <Flex className="form_layout" vAlign="start" gap="gap.large">
                <FlexItem grow styles={{maxWidth: "calc(100% - 530px)"}}>
                    <Flex column className="formContentContainer">
                        <Header as="h3" content={this.props.header} styles={{marginBottom: "0"}}/>
                        {this.props.enableCopilot &&
                            <CopilotContainer
                                locale={this.props.data.id}
                                maxLength={maxLength}
                                onTitleGenerated={this.props.onTitleChanged}
                                onMessageGenerated={this.props.onSummaryChanged}
                            />
                        }
                        {/* Title */}
                        <EditInput
                            inverted
                            required
                            max={100}
                            value={this.props.data.title}
                            label={languageObject.translate("TitleText")}
                            setValue={this.props.onTitleChanged}
                            autoFocus
                        />
                        {/* Bannière */}
                        <Flex gap={"gap.small"} vAlign={"end"}>
                            <Flex fill onClick={this.handleShowBannerBrowser}>
                                <EditInput
                                    inverted
                                    disabled
                                    label={languageObject.translate("Banner")}
                                    value={this.props.data.banner?.imageLink ?
                                        this.props.data.banner?.imageLink.replace(blobsRootFolderName, "/")
                                        :
                                        this.props.data.bannerLink as string
                                    }
                                />
                            </Flex>
                            <Button
                                content={languageObject.translate("Browse")}
                                icon={<ImageLibraryIcon outline/>}
                                onClick={this.handleShowBannerBrowser}
                            />
                            {this.props.data.banner?.imageLink &&
                                <Button
                                    content={languageObject.translate("Delete")}
                                    icon={<TrashCanIcon outline/>}
                                    onClick={this.handleDeleteBanner}
                                    className={"delete_button"}
                                />
                            }
                        </Flex>
                        {/* Message */}
                        <RichTextEditor
                            max={maxLength}
                            id={this.props.data.id}
                            value={this.props.data.message ?? ""}
                            onChange={this.props.onSummaryChanged}
                            label={languageObject.translate("Message")}
                            disableToolbar={isWindowsMessage}
                            inputHeight={200}
                        />
                        {/* Image */}
                        <Flex gap={"gap.small"} vAlign={"end"}>
                            <Flex fill onClick={this.handleShowImageBrowser}>
                                <EditInput
                                    inverted
                                    disabled
                                    label={languageObject.translate("Image")}
                                    value={this.props.data.image?.imageLink ?
                                        this.props.data.image?.imageLink.replace(blobsRootFolderName, "/")
                                        :
                                        this.props.data.imageLink as string
                                    }
                                />
                            </Flex>
                            <Button
                                content={languageObject.translate("Browse")}
                                icon={<ImageLibraryIcon outline/>}
                                onClick={this.handleShowImageBrowser}
                            />
                            {this.props.enableCopilot &&
                                <SuggestionsPopup
                                    message={this.props.data.message ?? ""}
                                    onSuggestionSelected={this.handleImageSelection}
                                />
                            }
                            {this.props.data.image?.imageLink &&
                                <Button
                                    content={languageObject.translate("Delete")}
                                    icon={<TrashCanIcon outline/>}
                                    onClick={this.handleDeleteImage}
                                    className={"delete_button"}
                                />
                            }
                        </Flex>
                        {/* Sub message */}
                        {!isWindowsMessage && (
                            <RichTextEditor
                                max={maxLength}
                                id={this.props.data.id}
                                value={this.props.data.subMessage ?? ""}
                                onChange={this.props.onSubMessageChanged}
                                label={languageObject.translate("SubMessage")}
                                disableToolbar={isWindowsMessage}
                                inputHeight={200}
                            />
                        )}
                        {/* Author */}
                        <EditInput
                            inverted
                            max={100}
                            value={this.props.data.author}
                            label={languageObject.translate("Author")}
                            setValue={this.props.onAuthorChanged}
                        />
                        {/* Ask user feedback */}
                        <Checkbox
                            checked={this.props.data.askFeedback}
                            className={"form-input"}
                            label={languageObject.translate("AskUserFeedback")} toggle
                            onClick={() => this.props.onAskFeedbackChange(!this.props.data.askFeedback)}
                        />
                        {/* Buttons */}
                        {this.props.data.buttons.length > 0 &&
                            <Accordion
                                className={"form-input"}
                                expanded
                                activeIndex={this.getOpenedButtonsIndexes()}
                                panels={this.props.data.buttons.map((button, index) => {
                                    const isValid = this.isButtonDataValid(button.id ?? "");
                                    return {
                                        title: {
                                            key: "accordion_button_title_" + index,
                                            content: (
                                                <Flex gap={"gap.smaller"} vAlign={"center"}>
                                                    <Text
                                                        content={languageObject.translate("Button") + " " + (index + 1)}/>
                                                    {isValid ? null :
                                                        <ExclamationCircleIcon styles={{color: "red"}} outline/>}
                                                </Flex>
                                            ),
                                            accordionContentId: "accordion_button_content_" + index,
                                            onClick: () => this.handleButtonTitleClick(button.id ?? ""),
                                        },
                                        content: {
                                            key: "accordion_button_content_" + index,
                                            content: this.renderButtonAccordionContent(index),
                                            accordionTitleId: "accordion_button_title_" + index,
                                        }
                                    }
                                })}/>
                        }
                        <Button
                            className={"form-input"}
                            fluid
                            disabled={!this.areButtonsValid() || !this.props.canAddButtons()}
                            styles={{marginBottom: "20px"}}
                            content={languageObject.translate("AddButton")}
                            icon={<AddIcon outline/>}
                            onClick={this.props.onAddButton}
                        />
                    </Flex>
                </FlexItem>
                <FlexItem styles={{minWidth: "500px", maxWidth: "500px"}}>
                    {this.renderMessagePreview()}
                </FlexItem>
                {this.renderImageBrowser()}
                {this.renderBannerBrowser()}
            </Flex>
        );
    }

    public renderMessagePreview = () => {
        if (this.props.channel === IMessageChannel.Channel_Teams || this.props.channel === IMessageChannel.Channel_GPTPro)
            return <div className="adaptiveCardContainer"/>;
        return (
            <Flex styles={{padding: "10px"}}>
                <WindowsNotification data={this.props.data} botName={this.props.gptName}/>
            </Flex>
        )
    }

    public renderButtonAccordionContent = (index: number) => {
        const isWindowsChannel = this.props.channel === IMessageChannel.Channel_Windows;
        const button = this.props.data.buttons[index];
        const actionType = button.actionType ?? ButtonActionType.Link;
        return (
            <Flex column className={"button_accordion_content"}>
                <EditInput
                    inverted
                    max={50}
                    value={button.title ?? ""}
                    label={languageObject.translate("Title")}
                    errorMessage={(!button.title || button.title.length === 0) ? languageObject.translate("FieldRequired") : ""}
                    setValue={(value) => this.props.onBtnTitleChanged(index, value)}
                    autoFocus
                />
                {isWindowsChannel &&
                    <FormRadioGroup
                        className={"form-input"}
                        label={languageObject.translate("Action")}
                        items={[
                            {
                                key: "link",
                                label: languageObject.translate("Link"),
                                onClick: () => this.props.onBtnActionTypeChanged(index, ButtonActionType.Link),
                                value: ButtonActionType.Link
                            },
                            {
                                key: "script",
                                label: languageObject.translate("PowershellScript"),
                                onClick: () => this.props.onBtnActionTypeChanged(index, ButtonActionType.Script),
                                value: ButtonActionType.Script
                            },
                        ]}
                        checkedValue={actionType}
                    />
                }
                {actionType === ButtonActionType.Link &&
                    <EditInput
                        inverted
                        max={2000}
                        value={button.link ?? ""}
                        label={languageObject.translate("URL")}
                        errorMessage={!button.link?.startsWith("https://") ? languageObject.translate("ErrorURLMessage") : ""}
                        setValue={(value) => this.props.onBtnLinkChanged(index, value)}
                    />
                }
                {isWindowsChannel && actionType === ButtonActionType.Script &&
                    <FilePickerButton
                        value={button.script}
                        handleFileSelected={(script) => this.props.onBtnScriptChanged(index, script)}
                        label={languageObject.translate("PowershellScript")}
                        buttonLabel={languageObject.translate("SelectScript")}
                        formats={[".ps1"]}
                    />
                }
                <Flex hAlign={"end"} className={"form-input"} gap={"gap.small"}>
                    <Button className={"delete_button"} content={languageObject.translate("Remove")}
                            icon={<TrashCanIcon outline/>}
                            onClick={() => this.props.onRemoveButton(index)}/>
                </Flex>
            </Flex>
        );
    }

    renderImageBrowser = () => {
        return (
            <ImageBrowserDialog
                show={this.state.showImageBrowser}
                selectedImage={this.props.data.image}
                availableImages={this.props.galleryImages}
                handleSelectImage={this.handleImageSelection}
                onHide={() => this.setState({showImageBrowser: false})}
                onImagesUpdated={this.handleImagesUpdate}
            />
        )
    }

    renderBannerBrowser = () => {
        return (
            <ImageBrowserDialog
                show={this.state.showBannerBrowser}
                selectedImage={this.props.data.banner}
                availableImages={this.props.galleryImages}
                handleSelectImage={this.handleBannerSelection}
                onHide={() => this.setState({showBannerBrowser: false})}
                onImagesUpdated={this.handleImagesUpdate}
            />
        )
    }
}

export default withRouter(CreateMessage)